import { getUrlVars, inIFrame, InitFramelessEmbed, InitIframeEmbed, MaybeRedirectToScaffolding } from './init';
import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AppSettings } from './app/app.settings';
import { DynamicRouting } from './app/dynamic.routing';
import { environment, setEnv } from './environments/environment';
import { AppModule } from './app/app.module';
import { PaymentGateway, recaptcha } from '@idonatedev/idonate-sdk';
declare const DD_RUM: any;

let environmentConfigPromise;

function configureApp() {
  environmentConfigPromise = fetch(`/env_vars.json?899d4733-8c23-4ba9-b6c7-b07ad444bf4c`, {
    method: 'GET',
    mode: 'no-cors'
  })
    .then(response => {
      if (response.ok) {
        return response.json();
      }
      console.error('response not ok');
      return Promise.reject(response.text());
    })
    .catch(error => {
      console.error('Failed to load env_vars: ', error);
    });
}

function runApp() {
  DD_RUM.onReady(function() {
    DD_RUM.init({
      clientToken: 'pubca8ad4abe68302f81352046907699b60',
      applicationId: 'd261d454-46da-4057-836e-2b506c43673a',
      site: 'datadoghq.com',
      service: 'single-page-embed',
      env: environment.sentry && environment.sentry.environment || 'development',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      defaultPrivacyLevel: 'allow',
      usePartitionedCrossSiteSessionCookie: true,
      allowedTracingUrls: [/^https:\/\/api\.(dev-|qa-)?idonate\.com/],
    });
  });
  if (environment.production) {
    enableProdMode();
  }
  if (inIFrame(window)) {
    // in-iframe embeds are driven by the host page's idonate.js loader
    InitIframeEmbed(initEmbed);
  } else {
    // app6-first (ID-1887): boot the legacy frameless embed right away; in
    // parallel ask should-scaffold and bounce to the scaffolding-hosted
    // page (query params forwarded as-is) when the embed migrated
    MaybeRedirectToScaffolding(getUrlVars());
    InitFramelessEmbed(initEmbed);
  }
}

function initEmbed(data: any) {
  /**
   * prepare to start the Angular app:
   * - rewrite & translate "/fetch"-ish data into pre-boot configuration
   * - build a route map based on what components are enabled
   * - inject global scripts that require configuration (e.g. googlemaps)
   * - start Angular, passing original config in as AppSettings
   */

  // `data` is (equivalent to) raw JSON from /fetch call - normally passed in
  // by idonatelib (not SDK - yet) via postMessage, can also be augmented
  // by URL params and data attributes, at least.

  const properties = data.embed_instance.properties;
  const bypassFraudChecks = data.embed_instance.bypass_fraud_checks || false;

  // Only inject reCaptcha script if fraud checks are not bypassed
  if (!bypassFraudChecks) {
    recaptcha.injectScript();
  }

  const features = data.embed_instance.organization.features;
  const campaign = data.embed_instance.organization.campaign;

  // retrieve and store pertinent gateway details
  const paypalGatewayId =
    data.embed_instance.organization.attributes.paypal_payment_gateway_id;

  const digitalWalletGatewayId =
    data.embed_instance.organization.attributes
      .digital_wallet_payment_gateway_id;

  const default_gateway_id =
    data.embed_instance.organization.attributes.config.payment
      .default_payment_gateway_id;

  // load available_gateways and reformat into SDK's types.
  const availableGateways: PaymentGateway[] = data.embed_instance.organization.available_gateways.map(
    gateway => ({
      id: gateway.id,
      backendName: gateway.backend_name,
      merchantId: gateway.gateway_config
        ? gateway.gateway_config.merchant_id
        : null,
      baseUrl: gateway.gateway_config
        ? gateway.gateway_config.base_url || null
        : null,
      spreedlyEnvironmentKey: gateway.gateway_config
        ? gateway.gateway_config.environment_key || null
        : null,
      // PayPalTokenizer reads its config snake_case off gateway.config,
      // so for paypal_checkout we forward the raw fields it expects.
      config: gateway.backend_name === 'paypal_checkout' && gateway.gateway_config
        ? {
            client_id: gateway.gateway_config.client_id,
            sandbox_mode: gateway.gateway_config.sandbox_mode,
            merchant_id: gateway.gateway_config.merchant_id,
            merchant_id_in_paypal: gateway.gateway_config.merchant_id_in_paypal,
          }
        : undefined,
    })
  );

  const primaryGateway: PaymentGateway = availableGateways.find(
    gateway => gateway.id === default_gateway_id
  );

  const paypalGateway: PaymentGateway = availableGateways.find(
    gateway => gateway.id === paypalGatewayId
  );

  const digitalWalletGateway: PaymentGateway = availableGateways.find(
    gateway => gateway.id === digitalWalletGatewayId
  );

  // build a lookup table to be used to build the route map
  // TODO : build the component array directly, this lookup table is just adding a layer of noise.
  const routeMap = {};
  let i = 0;
  for (const value of properties.components) {
    if (value === 'tribute/gift') {
      if (
        features.includes('tribute') &&
        campaign &&
        campaign.tribute_definitions &&
        campaign.tribute_definitions.length > 0
      ) {
        routeMap['tribute'] = `${++i}`;
      } else if (
        features.includes('thank_you') &&
        campaign &&
        campaign.gifts &&
        campaign.gifts.length > 0
      ) {
        routeMap['gift'] = `${++i}`;
      }
    } else if (value === 'corporate_matching') {
      if (features.includes('corporate_matching')) {
        routeMap[value] = `${++i}`;
      }
    } else {
      routeMap[value] = `${++i}`;
    }
  }

  routeMap['testimonial'] = !!(
    properties.testimonials && properties.testimonials.cash
  );
  routeMap['impact'] =
    !!properties.impact_equation_display &&
    properties.impact_equation_display !== 'none';
  routeMap['login'] = !!properties.enable_login;

  const thankYouRouteMap = {};
  for (const value of properties.thank_you.components) {
    thankYouRouteMap[value] = true;
  }
  thankYouRouteMap['impact'] = !!properties.impact_equation_display;

  const mapsScript = document.createElement('script');
  mapsScript.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
    environment.google.places
  )}&libraries=places&sensor=false&language=en`;
  document.head.appendChild(mapsScript);

  //
  platformBrowserDynamic([
    {
      provide: AppSettings,
      useValue: new AppSettings({
        ...data.embed_instance,
        donor_portal_url: data.donor_portal_url,
        donation: data.donation,
        token: data.token,
        payerId: data.payerId,
        // AppSettings stores `any`thing you send it...
        paypalGateway,
        primaryGateway,
        digitalWalletGateway,
        paymentGateway: primaryGateway,  // For credit card payments
        achGateway: primaryGateway,      // For ACH/bank account payments (using same gateway for now)
        bypassFraudChecks
      })
    },
    {
      provide: DynamicRouting,
      useValue: new DynamicRouting(routeMap, thankYouRouteMap)
    }
  ])
    .bootstrapModule(AppModule)
    .catch(err => console.log(err))
    .then(() => {});
}

configureApp();
environmentConfigPromise.then(config => {
  setEnv(config);
  runApp();
});
